import { Drawer, Flex, List, Typography } from 'antd'
import { FC } from 'react';
import { useIntl } from 'react-intl'
import { AVAILABLE_LANGUAGES, Language } from 'shared/const/languages';
import 'flag-icons/css/flag-icons.min.css';
import classes from './style.module.css';
import { CheckOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getLanguage } from 'shared/config/store/selectors/app';
import { useChangeLanguageMutation } from '../../../../pages/profile/api';
import { setActivity, setLanguage } from 'shared/config/store/reducers/appSlice';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { ApiError } from 'shared/model/types';

// Import translation files directly
import enTranslations from 'shared/libs/intl/translates/en.json';
import deTranslations from 'shared/libs/intl/translates/de.json';
import ruTranslations from 'shared/libs/intl/translates/ru.json';
import czTranslations from 'shared/libs/intl/translates/cz.json';
import plTranslations from 'shared/libs/intl/translates/pl.json';
import nlTranslations from 'shared/libs/intl/translates/nl.json';
import lvTranslations from 'shared/libs/intl/translates/lv.json';
import ltTranslations from 'shared/libs/intl/translates/lt.json';
import frTranslations from 'shared/libs/intl/translates/fr.json';
import itTranslations from 'shared/libs/intl/translates/it.json';
import esTranslations from 'shared/libs/intl/translates/es.json';
import eeTranslations from 'shared/libs/intl/translates/ee.json';
import bgTranslations from 'shared/libs/intl/translates/bg.json';

const translations = {
  en: enTranslations,
  de: deTranslations,
  ru: ruTranslations,
  cz: czTranslations,
  pl: plTranslations,
  nl: nlTranslations,
  lv: lvTranslations,
  lt: ltTranslations,
  fr: frTranslations,
  it: itTranslations,
  es: esTranslations,
  ee: eeTranslations,
  bg: bgTranslations,
};


type Props = {
  open: boolean;
  onClose: () => unknown;
};

type ListItem = {
  id: string;
  name: string;
  flagId: string;
}

const LanguageDrawer: FC<Props> = ({ open, onClose }) => {
  const { formatMessage: t } = useIntl();
  const language = useSelector(getLanguage);
  const [changeLanguage] = useChangeLanguageMutation();
  const dispatch = useDispatch();
  const { showSuccess, showError } = useMessage();

  const mappedLanguage: ListItem[] = Object.values(AVAILABLE_LANGUAGES).map((l) => ({ id: l.id, flagId: l.flagId, name: l.name }))

  const onSelectLanguage = (languageId: Language) => {
    if (!languageId) return;

    dispatch(setActivity(true));
    changeLanguage(languageId)
      .unwrap()
      .then(() => {
        dispatch(setLanguage(languageId))
        // Get the success message in the new language directly from translations
        const successMessage = translations[languageId]?.['toasts.language.success.edit.title'] || 
                              translations.en['toasts.language.success.edit.title'];
        showSuccess?.(successMessage);
        onClose();
      })
      .catch((err: ApiError) => {
        console.log(err);
        showError?.(t({ id: 'toasts.language.error.edit.title' }));
      })
      .finally(() => {
        dispatch(setActivity(false));
      })
  }

  const renderListItem = (item: ListItem) => {
    const isSelected = item.id === language;

    return (
      <List.Item onClick={() => onSelectLanguage(item.id as Language)} className={classes.listItemWrapper}>
        <Flex justify='space-between'>
          <Flex gap={12} align='center'>
            <span style={{ borderRadius: 6, fontSize: 21 }} className={`fi fi-${item.flagId}`} />
            <Typography.Text className={classes.text}>{item.name}</Typography.Text>
          </Flex>
          {isSelected && <CheckOutlined />}
        </Flex>
      </List.Item>
    )
  }

  return (
    <Drawer
      title={t({ id: 'settings.language.title' })}
      open={open}
      closable
      onClose={onClose}
      width={560}>
      <div>
        <List
          itemLayout='vertical'
          dataSource={mappedLanguage}
          split={false}
          renderItem={renderListItem}
        />
      </div>
    </Drawer>
  )
}

export default LanguageDrawer;